import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { type HandinPhase, type HandinProgress, IDLE, inProgress } from "@/handin_progress";

const LABELS: Record<HandinPhase, string> = {
    idle: "default",
    submitting: "evaluating",
    queued: "polling",
    running: "polling",
};

/**
 * Submit button for the new hand-in flow.
 * Renders its icon, label and disabled state from the phase in `progress`, which d-handin-card sets:
 *   idle: enabled, shows Submit label.
 *   submitting: disabled, shows Handing in… label with spinner.
 *   queued and running: disabled, shows Evaluating… label with spinner.
 *
 * Styles: shared with .btn.btn-filled
 *
 * @element d-handin-button
 *
 * @prop {HandinProgress} progress - Where the hand-in is. d-handin-card sets it.
 *
 * @fires handin - Dispatched when the button is clicked while no hand-in is in progress.
 */
@customElement("d-handin-button")
export class HandinButton extends DodonaElement {
    @property({ attribute: false })
    progress: HandinProgress = IDLE;

    private onClick(): void {
        this.dispatchEvent(new CustomEvent("handin", { bubbles: true }));
    }

    // i18n-tasks-use t('js.handin_button.default') t('js.handin_button.evaluating') t('js.handin_button.polling')
    render(): TemplateResult {
        const busy = inProgress(this.progress);
        const icon = busy ? "mdi-timer-sand mdi-spin" : "mdi-send";
        return html`
            <button type="button" class="btn btn-filled with-icon" ?disabled=${busy} @click=${this.onClick}>
                <i class="mdi ${icon}" aria-hidden="true"></i>
                <span class="btn-label">${I18n.t(`js.handin_button.${LABELS[this.progress.phase]}`)}</span>
            </button>
        `;
    }
}
